import { Link } from 'react-router';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { CarouselNavArrow } from '@/components/CarouselNavArrow';
import { CarouselNavDots } from '@/components/CarouselNavDots';
import { useCarouselData } from '@/hooks/useCarouselData';
import { Skeleton } from '@/components/ui/skeleton';
import { themeColorLinkClassNames } from '@/components/ui/utils';
import { hubSectionElementId, type HubSection, type HubSectionData } from '@/components/Hub/hubSectionTypes';
import { HubSectionEndCard } from '@/components/Hub/HubSectionEndCard';
import { HUB_ALTERNATE_SURFACE_CLASS } from '@/components/Hub/hubTheme';
import { trackingAttributes, type Tracking } from '@/lib/heap/trackingAttributes';
import { cn } from '@/lib/utils';
import 'material-symbols';

/** The shared arrow is sized for a full-width page. Inside a sheet it needs to be smaller on a phone. */
const NAV_ARROW_CLASS = 'w-8 h-8 md:w-11 md:h-11 [&_svg]:size-5 md:[&_svg]:size-7';

/**
 * The section spans the full sheet, canceling the body's own `px-6` so a tinted section reaches
 * both edges and the carousel's neighboring slides run off them. Everything except the carousel
 * puts that inset back.
 */
const BLEED_CLASS = '-mx-6';

/** The widest ring any section draws outside a card: a 2px line 2px clear of its edge. */
const FOCUS_RING_ROOM_CLASS = 'py-1 -my-1';
const INSET_CLASS = 'px-6';

const spotlightArrowTracking = (direction: 'prev' | 'next'): Tracking => ({
    click: direction === 'prev' ? 'Hub Spotlight Carousel Previous Clicked' : 'Hub Spotlight Carousel Next Clicked',
    location: 'hub-sheet',
    buttonName: direction === 'prev' ? 'spotlight-carousel-previous' : 'spotlight-carousel-next',
});

const sectionArrowTracking = (section: HubSection, direction: 'prev' | 'next'): Tracking =>
    section.id === 'spotlight'
        ? spotlightArrowTracking(direction)
        : {
                click: `Hub ${section.title} ${direction === 'prev' ? 'Previous' : 'Next'} Clicked`,
                location: 'hub-sheet',
                buttonName: `hub-${section.id}-${direction === 'prev' ? 'previous' : 'next'}`,
            };

/**
 * The centered slide reads as the one in focus and its neighbors sit back. A transform so the
 * slides keep their layout box and the ones either side stay where the carousel put them. Each
 * neighbor shrinks toward the focused slide, or it would pull its own near edge out of the peek.
 */
const slideScaleClass = (index: number, current: number) =>
    cn('transition-transform duration-300', index === current
        ? 'origin-center scale-100'
        : cn('scale-90', index < current ? 'origin-right' : 'origin-left'));

export interface HubCarouselSectionProps {
    section: HubSection;
    data: HubSectionData;
    /** Ignored when the section sets its own `surfaceClassName`. */
    hasAlternateSurface?: boolean;
}

export const HubCarouselSection = ({ section, data, hasAlternateSurface = false }: HubCarouselSectionProps) => {
    const { count, current, api, setApi, canScrollPrev, canScrollNext } = useCarouselData();
    const { slides, isLoading } = data;
    const surfaceClassName = section.surfaceClassName ?? (hasAlternateSurface ? HUB_ALTERNATE_SURFACE_CLASS : undefined);

    const endCard = section.endCard
        ? (
                <CarouselItem
                    key="end-card"
                    // `self-center` because the card is a fixed height and the slides beside it
                    // are not. Without it the row's stretch leaves it hanging from the top.
                    className={cn('self-center pl-3', section.itemBasisClassName, slideScaleClass(slides.length, current))}
                >
                    <HubSectionEndCard endCard={section.endCard} sectionId={section.id} sectionTitle={section.title} />
                </CarouselItem>
            )
        : null;

    return (
        <section
            id={hubSectionElementId(section.id)}
            tabIndex={-1}
            className={cn(
                'flex flex-col gap-3 outline-none',
                BLEED_CLASS,
                surfaceClassName && cn('py-4', surfaceClassName),
            )}
            aria-labelledby={`hub-section-${section.id}`}
        >
            <div className={cn('flex flex-col gap-1', INSET_CLASS)}>
                <h3 id={`hub-section-${section.id}`} className="type-headline-xsmall">
                    {section.title}
                </h3>
                <p className="type-section-description text-muted-foreground dark:text-gz-neutral-200">
                    {section.blurb}
                </p>
            </div>

            {isLoading
                ? (
                        <div className={cn('flex gap-3', INSET_CLASS)}>
                            <Skeleton className="h-40 flex-1 rounded-xl" />
                            <Skeleton className="hidden h-40 flex-1 rounded-xl sm:block" />
                        </div>
                    )
                : (
                        <>
                            <Carousel
                                setApi={setApi}
                                className="w-full"
                                // One slide centered with its neighbors peeking. `containScroll: false`
                                // lets the first and last slides center too. Trimming the snaps would
                                // pin them to the edges and lose the peek where the carousel starts.
                                opts={{ align: 'center', containScroll: false }}
                                aria-label={`${section.title} carousel`}
                            >
                                <CarouselContent
                                    className="-ml-3"
                                    // Embla's viewport clips and a slide is exactly as tall as it,
                                    // so a focus ring, which paints outside the border box, lost its
                                    // top and bottom edges. The padding is the room it needs and the
                                    // matching negative margin keeps the section's height.
                                    viewportClassName={FOCUS_RING_ROOM_CLASS}
                                >
                                    {slides.map((slide, index) => (
                                        <CarouselItem
                                            key={slide.id}
                                            aria-label={`slide ${index + 1} of ${slides.length}`}
                                            className={cn('pl-3', section.itemBasisClassName, slideScaleClass(index, current))}
                                        >
                                            {slide.render(index === current)}
                                        </CarouselItem>
                                    ))}
                                    {endCard}
                                </CarouselContent>
                            </Carousel>
                            <div className={cn('relative mt-4 flex min-h-8 w-full items-center justify-center md:min-h-11', INSET_CLASS)}>
                                {canScrollPrev && (
                                    <CarouselNavArrow
                                        direction="prev"
                                        onClick={() => api?.scrollPrev()}
                                        ariaLabel={`Previous ${section.title} item`}
                                        tracking={sectionArrowTracking(section, 'prev')}
                                        className={cn(NAV_ARROW_CLASS, 'absolute left-6 top-1/2 -translate-y-1/2')}
                                    />
                                )}
                                <CarouselNavDots count={count} index={current} api={api} className="mt-0" />
                                {canScrollNext && (
                                    <CarouselNavArrow
                                        direction="next"
                                        onClick={() => api?.scrollNext()}
                                        ariaLabel={`Next ${section.title} item`}
                                        tracking={sectionArrowTracking(section, 'next')}
                                        className={cn(NAV_ARROW_CLASS, 'absolute right-6 top-1/2 -translate-y-1/2')}
                                    />
                                )}
                            </div>
                            {section.viewAll && (
                                <Link
                                    to={section.viewAll.to}
                                    className={cn(
                                        'mt-4 mr-6 self-end inline-flex items-center gap-1.5 type-body-small font-medium',
                                        themeColorLinkClassNames,
                                        'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gz-bluebell-500',
                                    )}
                                    {...trackingAttributes({
                                        click: `Hub ${section.title} View All Clicked`,
                                        location: 'hub-sheet',
                                        buttonName: `hub-${section.id}-view-all`,
                                    })}
                                >
                                    {section.viewAll.label}
                                    {/* The glyph's em box is taller than the label's so center the
                                        icon on its own box rather than letting it sit on the baseline. */}
                                    <span
                                        className="material-symbols-rounded flex items-center text-lg leading-none"
                                        aria-hidden
                                    >
                                        open_in_new
                                    </span>
                                </Link>
                            )}
                        </>
                    )}
        </section>
    );
};
